import React, { useCallback, useEffect, useState } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Hidden, useMediaQuery, useTheme } from '@material-ui/core';

import { loadOrgAnnouncements } from './actions/announcements/load-org-announcements';
import { loadEmployeeGroups } from '../employee-groups/actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../employee-groups/employee-group-selectors';
import {
  EMPLOYEE_STATUS,
  EmployeeStatus,
  loadOrgEmployeesByStatus,
} from '../employees/actions/load-org-employees-by-status';
import { loadEmployeeMessages } from '../messages/actions/load-employee-messages';
import { loadOrg } from '../organization/actions/load-org';
import { userSelector } from '../reducers/current-user-selectors';
import {
  employeesForOrgIdSelector,
  employeesStateForOrgIdAndAllStatusSelector,
} from '../reducers/employee-selectors';
import {
  announcementsForOrgIdSelector,
  announcementsForOrgIdStatusSelector,
  employeeMessagesForOrgIdSelector,
  employeeMessagesForOrgIdStatusSelector,
} from '../reducers/message-selectors';
import { orgSelector } from '../reducers/org-selectors';
import { RequestState } from '../reducers/utils/state-manager';

import { EmployeeNotificationsWarning } from '../employees/employee-notifications-warning';
import { EmployeeProfileDialog } from '../employees/profile/employee-profile-dialog';
import { UiBreadcrumbHeader } from '../ui/ui-breadcrumb-header';
import { UiButton } from '../ui/ui-button';
import * as WebRoutes from '../web-routes';
import { employeeMessagesRoute } from '../web-routes';

import { isEmployeeMessage } from './employee-message-utils';
import { EmployeeMessagesEmptyState } from './message-composer/employee-messages-empty-state';
import { MessageThread } from './message-thread/message-thread';
import { RecipientList } from './recipient-list/recipient-list';

import { allEmployeesKey, allEmployeesLabel } from '../util/all-employees-option';
import { HeaderButtonAddIcon } from '../util/material-design-icons';
import { VCardPopoverIcon } from '../widgets/contact-card/v-card-popover-icon';
import { LoadingMessage } from '../widgets/loading-message';

type InputProps = {
  orgId: number;
  queryParams: {
    employeeId?: string;
    groupId?: string;
    messageThreadPreviewScrollPosition?: string;
  };
};

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  user: userSelector(state),
  messages: employeeMessagesForOrgIdSelector(state, orgId),
  employees: employeesForOrgIdSelector(state, orgId),
  employeesStates: employeesStateForOrgIdAndAllStatusSelector(state, orgId),
  employeeOrgMessagesState: employeeMessagesForOrgIdStatusSelector(state, orgId),
  announcements: announcementsForOrgIdSelector(state, orgId),
  announcementsStatus: announcementsForOrgIdStatusSelector(state, orgId),
  employeeGroups: employeeGroupsForOrgIdSelector(state, orgId),
  org: orgSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    loadEmployeeMessages: () => dispatch(loadEmployeeMessages(orgId)),
    loadOrgEmployeesByStatus: (status: EmployeeStatus) =>
      dispatch(loadOrgEmployeesByStatus(orgId, status)),
    loadOrgAnnouncements: () => dispatch(loadOrgAnnouncements(orgId)),
    loadEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployeeMessagesPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    orgId,
    org,
    queryParams,
    user,
    messages,
    employees,
    employeesStates,
    announcements,
    employeeGroups,
    actions,
  } = props;

  const messageThreadPreviewRef: React.RefObject<HTMLDivElement> = React.createRef();

  const activeEmployeeId = queryParams?.employeeId ? parseInt(queryParams.employeeId, 10) : null;
  const activeGroupId = queryParams?.groupId || null;

  const [popupEmployeeId, setPopupEmployeeId] = useState<number | null>(null);
  const [isComposingNewMessage, setIsComposingNewMessage] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [showContactCard, setShowContactCard] = useState<boolean>(false);

  const history = useHistory();
  const theme = useTheme();
  // There seems to be a bug with useMediaQuery where on first load of component it always returns false.
  // If we disable server side rendering, it fixes this behavior. Generally this isn't an issue,
  // but because we are running setActiveId on initial load, this actually matters here
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });

  const messageThreadPreviewScrollPosition = queryParams?.messageThreadPreviewScrollPosition
    ? parseInt(queryParams.messageThreadPreviewScrollPosition, 10)
    : null;

  const setActiveId = useCallback(() => {
    const unreadMessages = messages.filter((m) => m.unread);
    if (unreadMessages.length) {
      history.replace(
        WebRoutes.employeeMessagesRoute({
          orgId,
          employeeId: unreadMessages[0].employeeId,
          messageThreadPreviewScrollPosition: messageThreadPreviewRef.current?.scrollTop,
        }),
      );
    } else {
      const allMessages = (messages as Array<EmployeeOrgMessage | AnnouncementSummary>).concat(
        announcements,
      );
      const firstReadMessage = allMessages.reduce((prev, current) => {
        if (!prev.sentAt || !current.sentAt) {
          return prev.sentAt ? prev : current;
        }
        return moment(prev.sentAt).isSameOrAfter(moment(current.sentAt)) ? prev : current;
      }, {} as EmployeeOrgMessage | AnnouncementSummary);
      if (firstReadMessage) {
        history.replace(
          WebRoutes.employeeMessagesRoute({
            orgId,
            employeeId: isEmployeeMessage(firstReadMessage) ? firstReadMessage.employeeId : null,
            groupId: isEmployeeMessage(firstReadMessage) ? null : firstReadMessage.employeeGroupId,
            messageThreadPreviewScrollPosition: messageThreadPreviewRef.current?.scrollTop,
          }),
        );
      }
    }
  }, [announcements, history, messageThreadPreviewRef, messages, orgId]);

  useEffect(() => {
    actions.loadEmployeeMessages().then(() => {
      if (messageThreadPreviewScrollPosition) {
        messageThreadPreviewRef.current?.scroll({
          top: messageThreadPreviewScrollPosition,
        });
      }
      actions.loadOrgEmployeesByStatus(EMPLOYEE_STATUS.CURRENT);
      actions.loadOrgEmployeesByStatus(EMPLOYEE_STATUS.FORMER);
      actions.loadOrgAnnouncements();
      actions.loadEmployeeGroups();
      actions.loadOrg();
    });
  }, [actions, messageThreadPreviewRef, messageThreadPreviewScrollPosition, orgId]);

  // Set activeId to show a contact thread when there is none, and we aren't on mobile
  useEffect(() => {
    const noSelectedMessage = messages && messages.length && !activeEmployeeId;
    const noSelectedAnnouncement =
      announcements && announcements.length && employeeGroups && !activeGroupId;

    if (!isOnMobile && noSelectedMessage && noSelectedAnnouncement) {
      setActiveId();
    }
  }, [
    activeEmployeeId,
    activeGroupId,
    announcements,
    employeeGroups,
    messages,
    isOnMobile,
    setActiveId,
  ]);

  const handleMessageClick =
    ({ employeeId, groupId }: { employeeId?: number | null; groupId?: string | number | null }) =>
    () => {
      showThreadForId({
        employeeId: employeeId || null,
        groupId: groupId || null,
      });
    };

  const handleNewMessageToggle = () => {
    setIsComposingNewMessage(!isComposingNewMessage);
  };

  const handleMessageSent = (employeeId: number) => {
    showThreadForId({ employeeId });
    setIsComposingNewMessage(false);
  };

  const handleEmployeeClick =
    (newPopupEmployeeId: number, newSelectedTab: string) =>
    (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e?.stopPropagation();
      setPopupEmployeeId(newPopupEmployeeId);
      setSelectedTab(newSelectedTab);
    };

  const handlePopupCloseClick = () => setPopupEmployeeId(null);

  const openShowContactCard = () => {
    setShowContactCard(true);
  };
  const closeShowContactCard = () => setShowContactCard(false);

  const showThreadForId = ({
    employeeId,
    groupId,
  }: {
    employeeId?: number | null;
    groupId?: string | number | null;
  }) => {
    history.replace(
      WebRoutes.employeeMessagesRoute({
        orgId,
        employeeId,
        groupId,
        messageThreadPreviewScrollPosition: messageThreadPreviewRef.current?.scrollTop,
      }),
    );
  };

  const employeesAreLoading = employeesStates.every(
    (requestState: RequestState) => !requestState || requestState.loading,
  );

  if (employeesAreLoading || !employees || !messages || !announcements || !employeeGroups) {
    return <LoadingMessage />;
  }

  // IF (01/14/20): Push an artificial 'All Employees' group onto the list, we don't want our
  // users to have to maintain that with an actual group
  const allGroups = [
    {
      employeeGroupId: allEmployeesKey(orgId),
      name: allEmployeesLabel(user),
      orgId,
    },
    ...employeeGroups,
  ];

  const activeEmployee = employees.find((e) => e.employeeId === activeEmployeeId);
  const activeGroup = allGroups.find((eg) => eg.employeeGroupId?.toString() === activeGroupId);

  const currentEmployees = employees.filter((e) => e.isCurrentEmployee);

  return (
    <div className="u-display-flex u-flexDirection-column u-height-100">
      {/* Desktop header */}
      <Hidden xsDown>
        <UiBreadcrumbHeader
          pageTitle="Employee Messages"
          titleAction={{
            name: '',
            handler: openShowContactCard,
            Icon: (
              <VCardPopoverIcon
                org={org}
                variant="employee"
                onClose={closeShowContactCard}
                isOpen={showContactCard}
              />
            ),
          }}
          customPrimaryAction={
            currentEmployees.length ? (
              <UiButton
                size="sm"
                onClick={handleNewMessageToggle}
                variant={!isComposingNewMessage ? 'primary' : 'secondary'}
                dataCy="new-message"
                startIcon={!isComposingNewMessage ? <HeaderButtonAddIcon /> : undefined}
              >
                {!isComposingNewMessage ? 'New Message' : 'Cancel'}
              </UiButton>
            ) : null
          }
        />
      </Hidden>
      {/* Mobile header */}
      <Hidden smUp>
        {activeEmployeeId || activeGroupId ? null : (
          <UiBreadcrumbHeader
            pageTitle={isComposingNewMessage ? 'New Message' : 'Employee Messages'}
            customPrimaryAction={
              currentEmployees.length ? (
                <UiButton
                  size="sm"
                  onClick={handleNewMessageToggle}
                  variant={!isComposingNewMessage ? 'primary' : 'secondary'}
                  dataCy="new-message"
                  startIcon={!isComposingNewMessage ? <HeaderButtonAddIcon /> : undefined}
                >
                  {!isComposingNewMessage ? 'New' : 'Cancel'}
                </UiButton>
              ) : null
            }
            backToPage={isComposingNewMessage ? employeeMessagesRoute({ orgId }) : undefined}
          />
        )}
      </Hidden>
      {messages.length === 0 &&
      announcements.length === 0 &&
      !isComposingNewMessage &&
      !activeEmployee &&
      !activeGroupId ? (
        <EmployeeMessagesEmptyState orgId={orgId} handleNewMessageToggle={handleNewMessageToggle} />
      ) : (
        <>
          <EmployeeNotificationsWarning orgId={orgId} />
          <div className="u-display-flex u-flexGrow-1 u-overflow-hidden">
            <RecipientList
              orgId={orgId}
              messages={messages}
              announcements={announcements}
              messageThreadPreviewRef={messageThreadPreviewRef}
              activeEmployeeId={activeEmployeeId}
              activeGroupId={activeGroupId}
              isComposingNewMessage={isComposingNewMessage}
              handleMessageClick={handleMessageClick}
            />
            <MessageThread
              orgId={orgId}
              activeEmployee={activeEmployee}
              activeGroup={activeGroup}
              isComposingNewMessage={isComposingNewMessage}
              handleNewMessageToggle={handleNewMessageToggle}
              handleMessageSent={handleMessageSent}
              handleEmployeeClick={handleEmployeeClick}
              handleMessageClick={handleMessageClick}
              currentEmployees={currentEmployees}
            />
            {popupEmployeeId && (
              <EmployeeProfileDialog
                orgId={orgId}
                employeeId={popupEmployeeId}
                onClose={handlePopupCloseClick}
                tab={selectedTab}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
});
